import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CopilotPane } from '../office-shared/copilot';
import { WordSlateEditor } from './WordSlateEditor';
import { TopOfDoc } from './TopOfDoc';
import { TopOfDocStyleB } from './TopOfDocStyleB';
import { TopOfDocStyleC } from './TopOfDocStyleC';
import { TopOfDocStyleD } from './TopOfDocStyleD';
import { FloatingCopilotButton } from './FloatingCopilotButton';
import { FloatingCopilotButtonStyleB } from './FloatingCopilotButtonStyleB';
import { FloatingCopilotButtonStyleC } from './FloatingCopilotButtonStyleC';
import { FloatingCopilotButtonStyleD } from './FloatingCopilotButtonStyleD';
import { useWordSlateEditor } from './WordSlateEditorContext';
import { useStyleContext } from '../office-shared/layout';

const useStyles = makeStyles({
  container: {
    position: 'absolute',
    top: '132px', // Header (48px) + Ribbon (84px) = 132px to be flush with ribbon bottom
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: '#F5F5F5', // Neutral/Background/3/Rest
    overflow: 'hidden',
    display: 'flex',
  },
  
  // Main content area - will shrink when copilot pane is open
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    transition: 'margin-right 0.3s ease',
  },
  
  mainContentWithCopilot: {
    marginRight: '380px', // Width of copilot pane - ensure main content doesn't overlap
  },
  
  // Copilot pane container
  copilotPaneContainer: {
    position: 'fixed',
    top: '132px',
    right: '0',
    bottom: '20px',
    width: '380px',
    zIndex: 100,
    // Ensure it doesn't overflow the viewport
    maxWidth: '100vw',
    overflow: 'visible',
  },
  
  // Floating Navigation Pane Button
  floatingButton: {
    position: 'fixed',
    top: '144px', // 132px (container start) + 12px spacing = 144px
    left: '8px',
    width: '40px',
    height: '40px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:active': {
      backgroundColor: '#e5e5e5',
    },
  },
  
  // Main Document Area
  mainArea: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F5F5F5',
  },
  
  documentContainer: {
    position: 'relative', // For FloatingCopilotButton positioning
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 24px 58px 24px', // 12px top spacing between ribbon and TopOfDoc, bottom: 58px for sticky footer
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  
  documentPage: {
    width: '816px', // Standard 8.5" page width at 96 DPI
    minHeight: '1056px', // Standard 11" page height at 96 DPI
    backgroundColor: 'white',
    borderRadius: '12px', // Match summary card corner radius
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '96px 72px', // 1" margins (96px) top/bottom, 0.75" (72px) left/right
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'text',
    flexShrink: 0,
    boxSizing: 'border-box', // Border inside
  },

  // Document Content Area - Slate rich text editor
  documentContent: {
    flex: '1 0 auto',
    width: '100%',
    minHeight: '800px',
  },

  // TopOfDoc container - scrolls with document, full width like ribbon, 12px gap to documentPage
  topOfDocContainer: {
    width: 'calc(100vw - 8px)', // Account for scrollbar
    maxWidth: 'calc(100vw - 8px)',
    minWidth: '816px', // Match slate editor width
    padding: '4px 4px', // Add vertical padding to allow shadow to show
    marginLeft: '-24px',
    marginRight: '-24px',
    marginBottom: '8px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  
  // TopOfDoc container for Style B - fixed width to match Slate editor
  topOfDocContainerStyleB: {
    width: '816px',
    maxWidth: '816px',
    minWidth: '816px',
    padding: '4px 0', // Vertical padding only
    marginLeft: '0',
    marginRight: '0',
    marginBottom: '8px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  
  // TopOfDoc container for Style C - fixed width to match Slate editor (same as Style B for now)
  topOfDocContainerStyleC: {
    width: '816px',
    maxWidth: '816px',
    minWidth: '816px',
    padding: '4px 0', // Vertical padding only
    marginLeft: '0',
    marginRight: '0',
    marginBottom: '8px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  
  // TopOfDoc hidden when Copilot pane is open
  topOfDocContainerHidden: {
    display: 'none',
  },
});

interface WordBodyProps {
  onTextChange?: (text: string) => void;
  isCopilotPaneOpen?: boolean;
  onCloseCopilotPane?: () => void;
  onCopilotClick?: () => void;
  onSummaryClick?: (summary: string) => void;
  onPillClick?: (pillText: string) => void;
  copilotInitialMessage?: { type: 'user' | 'assistant'; content: string };
}

export const WordBody: React.FC<WordBodyProps> = ({ 
  onTextChange,
  isCopilotPaneOpen = false, 
  onCloseCopilotPane,
  onCopilotClick,
  onSummaryClick,
  onPillClick,
  copilotInitialMessage
}) => {
  const styles = useStyles();
  const documentContainerRef = useRef<HTMLDivElement>(null);
  const topOfDocRef = useRef<HTMLDivElement>(null);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const { commands } = useWordSlateEditor();
  const { selectedStyle } = useStyleContext();
  
  // Get document content for Copilot context
  const getDocumentContent = useCallback(() => {
    try {
      return commands.getDocumentText();
    } catch {
      return '';
    }
  }, [commands]);
  
  // Scroll detection - show floating button when TopOfDoc scrolls out of view
  const handleScroll = useCallback(() => {
    if (documentContainerRef.current) {
      // Simply check the scroll position of the container
      // Show DAB when scrolled more than the height of TopOfDoc (approximately 60px)
      const scrollTop = documentContainerRef.current.scrollTop;
      const threshold = 60; // Approximately the height of TopOfDoc
      
      const shouldShowDab = scrollTop > threshold;
      setShowFloatingButton(shouldShowDab);
    }
  }, []);
  
  // Add scroll listener
  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <div className={styles.container}>
      {/* Main Content Area */}
      <div className={`${styles.mainContent} ${isCopilotPaneOpen ? styles.mainContentWithCopilot : ''}`}>
        <div className={styles.mainArea}>
          <div className={styles.documentContainer} ref={documentContainerRef}>
            {/* TopOfDoc - Copilot bar, scrolls with content, hidden when Copilot pane is open */}
            <div 
              ref={topOfDocRef}
              className={`${selectedStyle === 'styleB' ? styles.topOfDocContainerStyleB : selectedStyle === 'styleC' ? styles.topOfDocContainerStyleC : styles.topOfDocContainer} ${isCopilotPaneOpen ? styles.topOfDocContainerHidden : ''}`}
            >
              {selectedStyle === 'styleB' ? (
                <TopOfDocStyleB 
                  onCopilotClick={onCopilotClick}
                  onSummaryClick={onSummaryClick}
                  onMessageSend={onPillClick}
                />
              ) : selectedStyle === 'styleC' ? (
                <TopOfDocStyleC 
                  onCopilotClick={onCopilotClick}
                  onSummaryClick={onSummaryClick}
                  onMessageSend={onPillClick}
                  onPillClick={onPillClick}
                />
              ) : selectedStyle === 'styleD' ? (
                <TopOfDocStyleD 
                  onCopilotClick={onCopilotClick}
                  onPillClick={onPillClick}
                />
              ) : (
                <TopOfDoc 
                  onCopilotClick={onCopilotClick}
                  onSummaryClick={onSummaryClick}
                  onPillClick={onPillClick}
                  onMessageSend={onPillClick}
                />
              )}
            </div>
            
            <div className={styles.documentPage}>
              {/* Slate rich text editor */}
              <div className={styles.documentContent}>
                <WordSlateEditor 
                  placeholder="Start typing your document here..." 
                  onTextChange={onTextChange}
                />
              </div>
            </div>
            
            {/* Floating Copilot Button - appears when TopOfDoc scrolls out of view */}
            {selectedStyle === 'styleB' ? (
              <FloatingCopilotButtonStyleB 
                isVisible={showFloatingButton && !isCopilotPaneOpen}
                onPillClick={onPillClick}
                onDabClick={onCopilotClick}
              />
            ) : selectedStyle === 'styleC' ? (
              <FloatingCopilotButtonStyleC 
                isVisible={showFloatingButton && !isCopilotPaneOpen}
                onPillClick={onPillClick}
                onDabClick={onCopilotClick}
              />
            ) : selectedStyle === 'styleD' ? (
              <FloatingCopilotButtonStyleD 
                isVisible={showFloatingButton && !isCopilotPaneOpen}
                onPillClick={onPillClick}
                onDabClick={onCopilotClick}
              />
            ) : (
              <FloatingCopilotButton 
                isVisible={showFloatingButton && !isCopilotPaneOpen}
                onPillClick={onPillClick}
                onDabClick={onCopilotClick}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Copilot Pane */}
      {isCopilotPaneOpen && (
        <div className={styles.copilotPaneContainer}>
          <CopilotPane 
            isOpen={isCopilotPaneOpen} 
            onClose={onCloseCopilotPane || (() => {})}
            appType="word"
            initialMessage={copilotInitialMessage}
            documentContent={getDocumentContent()}
          />
        </div>
      )}
    </div>
  );
};

export default WordBody;
